import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { Constants } from '../model/common.model';

@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  constructor(private http: HttpClient) { }

  search(criteria: any) {
    return firstValueFrom(this.http.post<BusinessSearchResult>('/api/business/search', criteria));
  }

  get(id: string) {
    return firstValueFrom(this.http.get<any>('/api/business/get/' + id));
  }

  save(business: any) {
    return firstValueFrom(this.http.post<void>('/api/business/save', business));
  }

  resetApiKey(businessId: any) {
    return firstValueFrom(this.http.post<string>('/api/business/reset-apikey/' + businessId, null));
  }

  generateSetupCode(businessId: string) {
    return firstValueFrom(this.http.post<{ code: string }>('/api/britlink/setup-code/' + businessId, null));
  }

  delete(id: string, permanent: boolean) {
    return firstValueFrom(this.http.delete<void>('/api/business/delete/' + id + (permanent ? '/true' : '')));
  }

  getAll(criteria: any) {
    return firstValueFrom(this.http.get<{ List: any[], Total: number }>('/api/business/published', { params: criteria }));
  }

  importBusiness(url: string) {
    return firstValueFrom(this.http.post<any>('/api/business/import', { url }));
  }

  getManagementMenuItems(business: any, staff: any) {
    let items = <MenuItem[]>[];

    for (var p of Constants.BusinessPermissions.filter(_ => staff.Permissions.includes(_))) {
      switch (p) {
        case 'Order':
          items.push({ label: 'Orders', icon: 'pi pi-shopping-cart', routerLink: `/panel/order` });
          break;
        case 'Feedback':
          items.push({ label: 'Feedbacks', icon: 'pi pi-eye', routerLink: `/panel/feedback` });
          break;
        case 'Reservation':
          items.push({ label: 'Reservations', icon: 'pi pi-calendar', routerLink: `/panel/reservation` });
          break;
        case 'Availability':
          items.push({ label: 'Availability', icon: 'pi pi-eye', routerLink: `/panel/availability` });
          break;
        case 'Menu':
          items.push({ label: 'Menus', icon: 'pi pi-list', routerLink: `/panel/menu/${business.Id}` });
          items.push({ label: 'Menu Items', icon: 'pi pi-box', routerLink: `/panel/menu/${business.Id}/item` });
          items.push({ label: 'Item Options', icon: 'pi pi-palette', routerLink: `/panel/menu/${business.Id}/option` });
          items.push({ label: 'Promotions', icon: 'pi pi-tags', routerLink: `/panel/promotion/${business.Id}` });
          break;
        case 'Business':
          items.push({ label: 'Business', icon: 'pi pi-briefcase', routerLink: `/panel/${business.Id}` });
          items.push({ label: 'Report', icon: 'pi pi-chart-bar', routerLink: `/panel/report/${business.Id}` });
          break;
        case 'Payments':
          items.push({ label: 'Payments', icon: 'pi pi-money-bill', routerLink: `/panel/payments` });
          items.push({ label: 'Transacts', icon: 'pi pi-money-bill', routerLink: `/panel/transact/${business.Id}` });
          // items.push({ label: 'Invoices', icon: 'pi pi-money-bill', routerLink: `/panel/invoice/${business.Id}` });
          break;
      }
    }


    return items;
  }

  getAddressLookupSettings() {
    return firstValueFrom(this.http.get<any>('/api/business/address-lookup-settings'));
  }

  updateAddressLookupSettings(settings: any) {
    return firstValueFrom(this.http.post<void>('/api/business/address-lookup-settings', settings));
  }

  getSystemConfig() {
    return firstValueFrom(this.http.get<any>('/api/business/system-config'));
  }

  // Single request that returns everything the public landing page needs for its initial
  // render (homepage UI config + popular businesses, with the popular/latest fallback
  // resolved server-side). Replaces the system-config -> published -> published(fallback)
  // browser waterfall.
  getHome(postcode?: string) {
    const params: any = {};
    if (postcode) params.postcode = postcode;
    return firstValueFrom(this.http.get<HomeData>('/api/business/home', { params }));
  }
  updateSystemConfig(configs: any) {
    return firstValueFrom(this.http.post<void>('/api/business/system-config', configs));
  }

  getHeartbeat(businessId: string) {
    return lastValueFrom(this.http.get('/api/britlink/heartbeat/' + businessId));
  }
  getHeartbeats(businessIds: string[]) {
    return lastValueFrom(this.http.post<{ [businessId: string]: any }>('/api/britlink/heartbeat/batch', { List: businessIds }));
  }
}

export type BusinessSearchResult = { List: any[]; Total: number; };
export type HomeData = { UI: { [key: string]: string }; PopularBusinesses: any[]; };
